import { BASE_RULES_KEY, type RuleResponse, type RuleArray } from '@core/types'
import { inject } from '#imports'
// Relative on purpose: '@/' resolves against the consuming brand's srcDir, not this layer's.
import { PHONE_MASKS, getPhoneNumberStartDigits } from '../utils/phone'

interface UseRulesReturn {
    required(message?: string): (value?: string | number | boolean) => RuleResponse
    PHONE: RuleArray
    FIRSTNAME: RuleArray
    LASTNAME: RuleArray
    EMAIL: RuleArray
    EXISTING_PASSWORD: RuleArray
    NEW_PASSWORD: RuleArray
    BIRTHDATE: RuleArray
    BIRTHDATE_ZODIAC: RuleArray
    CITY: RuleArray
    ZIPCODE: RuleArray
    ADDRESS: RuleArray
    FULL_ADDRESS: RuleArray
    TURNSTILE: RuleArray
    USERNAME: RuleArray
}

export const useRules = (): UseRulesReturn => {
    // Global composer instead of useI18n(): this composable is also called from the
    // `phone-input` Nuxt plugin, where useI18n() throws (no component setup context).
    const { t } = useNuxtApp().$i18n

    if (import.meta.client) {
        const dayjs = useDayjs()

        const baseRules = inject(BASE_RULES_KEY, undefined)

        if (!baseRules) {
            throw new Error('Rules not provided!')
        }

        const {
            required,
            minLength,
            noNumbers,
            noSpecialCharacters,
            EMAIL,
            EXISTING_PASSWORD,
            NEW_PASSWORD,
        } = baseRules

        function birthdate(value?: string): RuleResponse {
            if (!value) return true
            if (value?.length !== 10) return true

            const validateFormat = 'DD/MM/YYYY'
            const validateString = value + validateFormat.slice(value.length)
            const validateValue = validateString
                .replaceAll('D', '0')
                .replace('MM', '01')
                .replaceAll('M', '1')
                .replaceAll('Y', '1')

            return dayjs(validateValue, validateFormat, true).isValid() || t('core.validationRules.birthDate.invalid')
        }

        function birthdateZodiac(value?: string): RuleResponse {
            if (!value) return true
            if (value?.length !== 10) return true

            const validateFormat = 'DD/MM/YYYY'
            const validateString = value + validateFormat.slice(value.length)
            const validateValue = validateString
                .replaceAll('D', '')
                .replace('MM', '')
                .replaceAll('M', '')
                .replaceAll('Y', '')

            const birthdate = dayjs(validateValue, validateFormat, true)

            if (!birthdate.isValid()) return t('core.validationRules.birthDate.invalid')

            const eighteenYearsAgo = dayjs().subtract(18, 'year').add(1, 'day')

            return birthdate.isBefore(eighteenYearsAgo, 'day') || 'Oops! 18+ players only'
        }

        function phone(value?: string): RuleResponse {
            if (!value) return true

            const selectedMask = PHONE_MASKS.find(mask => value.startsWith(getPhoneNumberStartDigits(mask.mask)))

            if (!selectedMask) return t('core.validationRules.phone.invalid')

            const isMatchingMaskLength = selectedMask.lengths.includes(value.length)

            return isMatchingMaskLength || `Phone numbers starting with ${getPhoneNumberStartDigits(selectedMask.mask)} must be ${selectedMask.lengths.join(' or ')} digits`
        }

        const PHONE: RuleArray = [
            required(t('core.validationRules.required.phone')),
            phone,
        ]

        const FIRSTNAME: RuleArray = [
            required(t('core.validationRules.required.firstName')),
            minLength(2, t('core.validationRules.minLength.name')),
            noNumbers,
            noSpecialCharacters,
        ]

        const LASTNAME: RuleArray = [
            required(t('core.validationRules.required.lastName')),
            minLength(2, t('core.validationRules.minLength.name')),
            noNumbers,
            noSpecialCharacters,
        ]

        const BIRTHDATE: RuleArray = [
            required(t('core.validationRules.required.birthDate')),
            minLength(10, t('core.validationRules.minLength.birthDate')),
            birthdate,
        ]

        const BIRTHDATE_ZODIAC: RuleArray = [
            required(t('core.validationRules.required.birthDate')),
            minLength(10, t('core.validationRules.birthDate.invalid')),
            birthdateZodiac,
        ]

        const CITY: RuleArray = [
            required(t('core.validationRules.required.city')),
        ]

        const ZIPCODE: RuleArray = [
            required(t('core.validationRules.required.zip')),
        ]

        const ADDRESS: RuleArray = [
            required(t('core.validationRules.required.address')),
        ]

        // Autocompleted addresses: Address + City + ZIP
        const FULL_ADDRESS: RuleArray = [
            required(t('core.validationRules.required.fullAddress')),
        ]

        // Cloudflare Turnstile | CAPTCHA Replacement Solution
        const TURNSTILE: RuleArray = [
            required(t('core.validationRules.required.turnstile')),
        ]

        const USERNAME: RuleArray = [
            required(),
        ]

        return {
            required,
            PHONE,
            FIRSTNAME,
            LASTNAME,
            EMAIL,
            EXISTING_PASSWORD,
            NEW_PASSWORD,
            BIRTHDATE,
            BIRTHDATE_ZODIAC,
            CITY,
            ZIPCODE,
            ADDRESS,
            FULL_ADDRESS,
            TURNSTILE,
            USERNAME,
        }
    }
    else {
        return {
            // Server stub: a no-op rule that always passes (validation runs client-side).
            required: () => () => true,
            PHONE: [],
            FIRSTNAME: [],
            LASTNAME: [],
            EMAIL: [],
            EXISTING_PASSWORD: [],
            NEW_PASSWORD: [],
            BIRTHDATE: [],
            BIRTHDATE_ZODIAC: [],
            CITY: [],
            ZIPCODE: [],
            ADDRESS: [],
            FULL_ADDRESS: [],
            TURNSTILE: [],
            USERNAME: [],
        }
    }
}
